// RUTA: src/components/fitness/CoachHubCard.tsx
// Hub unificado "Tu coach" — agrupa los 3 mensajes/CTAs que tienen
// que ver con el coach en un solo container armónico:
//   • Mensaje del coach (si lo escribió)
//   • Próxima evaluación + agregar al calendario (si tiene fecha)
//   • Reserva 1-on-1 (CTA o info de sesión próxima)
//
// Antes eran 3 cards sueltas con estilos diferentes que rompían
// la armonía visual. Ahora viven juntas, separadas solo por
// divisores sutiles, con el mismo border/bg/padding.

import { useEffect, useRef, useState } from 'react';
import { MessageSquare, CalendarCheck, CalendarPlus, Apple, Zap } from 'lucide-react';
import { UserProfile } from '@/types/fitness';
import { BookTrainingCard } from './BookTrainingCard';
import { SectionHeader } from './SectionHeader';
import { UserAvatar } from './UserAvatar';
import { supabase } from '@/integrations/supabase/client';
import { getPrimaryCoachId } from '@/lib/primaryCoach';
import marcoAvatar from '@/assets/marco-avatar-circle.png';

interface Props {
  perfil: UserProfile;
}

// Helpers — formatear fecha de evaluación en español
function formatEvalDate(iso?: string | null): string | null {
  if (!iso) return null;
  // Construir fecha local sin timezone shift
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('es-MX', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function CoachHubCard({ perfil }: Props) {
  const evalDate = formatEvalDate(perfil.nextEvaluationDate);
  const hasNotes = !!perfil.coachNotes;
  const hasEval = !!evalDate;

  // Coach principal del cliente (para el hero con foto + nombre)
  const [coach, setCoach] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const primaryId = await getPrimaryCoachId(user.id);
      if (cancel || !primaryId) return;
      const { data: prof } = await supabase
        .from('profiles').select('display_name')
        .eq('id', primaryId).maybeSingle();
      if (cancel) return;
      setCoach({
        id: primaryId,
        name: (prof as any)?.display_name || 'Coach',
      });
    })();
    return () => { cancel = true; };
  }, []);

  // Calendar export menu state
  const [calendarMenuOpen, setCalendarMenuOpen] = useState(false);
  const calendarMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!calendarMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (calendarMenuRef.current && !calendarMenuRef.current.contains(e.target as Node)) {
        setCalendarMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [calendarMenuOpen]);

  const eventTitle = 'Evaluación Smart Body — Insane Force Team';
  const eventDescription = 'Cita de evaluación con tu coach IFTMX.';

  const addToGoogleCalendar = () => {
    if (!perfil.nextEvaluationDate) return;
    const dateCompact = perfil.nextEvaluationDate.replace(/-/g, '');
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(eventTitle)}` +
      `&dates=${dateCompact}/${dateCompact}` +
      `&details=${encodeURIComponent(eventDescription)}`;
    window.open(url, '_blank');
    setCalendarMenuOpen(false);
  };

  const addToAppleCalendar = () => {
    if (!perfil.nextEvaluationDate) return;
    const dateCompact = perfil.nextEvaluationDate.replace(/-/g, '');
    const dtStamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//IFTMX//Coach App//ES',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:eval-${dateCompact}@iftmx.app`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART;VALUE=DATE:${dateCompact}`,
      `DTEND;VALUE=DATE:${dateCompact}`,
      `SUMMARY:${eventTitle}`,
      `DESCRIPTION:${eventDescription}`,
      'BEGIN:VALARM',
      'TRIGGER:-P1D',
      'ACTION:DISPLAY',
      'DESCRIPTION:Recordatorio: tu evaluación con el coach es mañana',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    window.location.href = blobUrl;
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    setCalendarMenuOpen(false);
  };

  return (
    <div className="space-y-0">
      {/* Header de sección estilo HISTORIAL/RUTINA — uniforme con el
          resto de la página. Reemplaza el header pequeño interno que
          existía antes (TU COACH plano sin jerarquía visual). */}
      <SectionHeader
        title="Tu Coach"
        subtitle="Mensajes · Evaluación · Reserva"
        icon={MessageSquare}
      />

      <section className="relative rounded-2xl overflow-hidden bg-card dark:bg-primary shadow-lg dark:shadow-2xl border border-border dark:border-transparent noise-overlay">
      {/* Gradient overlay — industrial en dark, sutil en light */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-card to-accent/5 dark:from-primary dark:via-primary/80 dark:to-accent/20 z-0"></div>
      {/* Carbon fibre texture — solo dark */}
      <div className="absolute inset-0 opacity-0 dark:opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] z-0"></div>
      {/* Radial glow accent */}
      <div className="absolute top-1/2 left-1/3 -translate-y-1/2 w-[400px] h-[400px] bg-accent/5 dark:bg-accent/8 rounded-full blur-[120px] z-0"></div>

      <div className="relative z-10">
        {/* ── Hero del coach: foto + estado + título ──────────── */}
        {coach && (
          <div className="px-4 md:px-6 pt-6 pb-5 flex flex-col items-center text-center border-b border-border dark:border-white/10">
            {/* Foto con glow accent + dot verde online.
             *  Para coaches específicos usamos un avatar local del
             *  asset (marco-avatar-circle.png) cuando el nombre
             *  coincide. Si el coach sube su foto a su profile en
             *  el futuro, UserAvatar la usará automáticamente. */}
            <div className="relative mb-3">
              <div className="rounded-full shadow-2xl shadow-accent/30 animate-pulse">
                <UserAvatar
                  userId={coach.id}
                  name={coach.name}
                  size="lg"
                  editable={false}
                  avatarUrl={/marco/i.test(coach.name) ? marcoAvatar : undefined}
                />
              </div>
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-400 rounded-full border-4 border-card dark:border-primary flex items-center justify-center">
                <Zap className="w-3 h-3 text-green-900" />
              </div>
            </div>
            <p className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground dark:text-primary-foreground/60 mb-2">
              En línea · Fitness &amp; Nutrición
            </p>
            <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight text-foreground dark:text-primary-foreground">
              Tu Coach: <span className="text-accent">{coach.name.split(' ')[0]}</span> 💪
            </h3>
            <p className="text-xs text-muted-foreground dark:text-primary-foreground/60 mt-1 max-w-md">
              Tu equipo Insane Force Team contigo en cada paso.
            </p>
          </div>
        )}

        {/* ── Subitem 1: Mensaje del coach ───────────────────── */}
        {hasNotes && (
          <div className="px-4 md:px-5 py-4 flex gap-3 border-b border-border dark:border-white/10">
            <div className="shrink-0 mt-0.5">
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <MessageSquare className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground dark:text-primary-foreground/50 mb-1">
                Mensaje para ti
              </p>
              <p className="text-sm leading-relaxed whitespace-pre-line text-foreground dark:text-primary-foreground/90">
                {perfil.coachNotes}
              </p>
            </div>
          </div>
        )}

        {/* ── Subitem 2: Próxima evaluación + agendar ────────── */}
        {hasEval && (
          <div className="px-4 md:px-5 py-4 flex items-start sm:items-center gap-3 border-b border-border dark:border-white/10">
            <div className="shrink-0">
              <div className="w-9 h-9 rounded-lg bg-accent/15 flex items-center justify-center">
                <CalendarCheck className="h-4 w-4 text-accent" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground dark:text-primary-foreground/50 mb-0.5">
                Próxima evaluación
              </p>
              <p className="text-sm font-black capitalize text-foreground dark:text-primary-foreground">{evalDate}</p>
            </div>
            <div className="relative shrink-0" ref={calendarMenuRef}>
              <button
                onClick={() => setCalendarMenuOpen(o => !o)}
                title="Agregar al calendario"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25 transition-colors text-[11px] font-black uppercase tracking-wide min-h-[36px]"
              >
                <CalendarPlus className="h-3.5 w-3.5" />
                Agendar
              </button>
              {calendarMenuOpen && (
                <div className="absolute right-0 top-full mt-2 z-30 w-56 bg-card border-2 border-border rounded-xl shadow-2xl overflow-hidden">
                  <button
                    onClick={addToGoogleCalendar}
                    className="w-full flex items-center gap-2.5 px-3 py-3 hover:bg-accent/10 transition-colors text-left min-h-[44px] border-b border-border"
                  >
                    <span className="text-lg shrink-0">📅</span>
                    <div className="flex-1">
                      <div className="text-sm font-bold">Google Calendar</div>
                      <div className="text-[10px] text-muted-foreground">Recomendado para Android y Chrome</div>
                    </div>
                  </button>
                  <button
                    onClick={addToAppleCalendar}
                    className="w-full flex items-center gap-2.5 px-3 py-3 hover:bg-accent/10 transition-colors text-left min-h-[44px]"
                  >
                    <Apple className="h-5 w-5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-sm font-bold">Apple Calendar</div>
                      <div className="text-[10px] text-muted-foreground">Recomendado para iPhone Safari</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Subitem 3: Reservar 1-on-1 ──────────────────────── */}
        <div className="px-4 md:px-5 py-3">
          <BookTrainingCard variant="promo" />
        </div>
      </div>
    </section>
    </div>
  );
}
